import React, { useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FullScreenModeContext } from "../../../shared/state/fullScreenModeContext";

const fullScreenBodyClass = "chart-full-screen-active";

/**
 * The popups ChartHOC.less lifts over the overlay, matched only while they are
 * open. Each of them closes itself on Escape without stopping the event, so a
 * chart that collapsed on the same keypress would pull the panel out from under
 * a menu the reader was only dismissing.
 */
const openPopupSelector = [
  ".ant-dropdown:not(.ant-dropdown-hidden)",
  ".ant-select-dropdown:not(.ant-select-dropdown-hidden)",
  ".ant-picker-dropdown:not(.ant-picker-dropdown-hidden)",
].join(", ");

/**
 * Expanding a chart to fill the page.
 *
 * Kept here rather than in whichever component happens to draw the panel,
 * because more than one does: charts wrapped by ChartHOC get their panel from
 * COREContainer, while dashboard widgets get theirs from WidgetPanelRender.
 * Both need the same overlay, the same page chrome folded away and the same
 * chart redraw, and only the panel each one draws differs.
 *
 * The styles it relies on live in ChartHOC.less.
 */
export const useChartFullScreen = () => {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  // The page layout owns the top header, so it has to be asked to fold it away
  // while a chart is expanded. Painting over it is not an option: the header is
  // fixed at z-index 2001 (PageLayout.less), above this overlay, and raising
  // the overlay past it would then bury `CORETooltip`, which sits at 2001 too.
  // The tooltips a chart draws for itself sit far lower and are lifted over the
  // overlay by ChartHOC.less instead.
  const { setIsFullScreenMode } = useContext(FullScreenModeContext);

  const toggleFullScreen = useCallback(
    () => setIsFullScreen((wasFullScreen) => !wasFullScreen),
    []
  );

  useEffect(() => {
    if (!isFullScreen) return;
    setIsFullScreenMode?.(true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Read by ChartHOC.less to lift body-level popups over the overlay.
    document.body.classList.add(fullScreenBodyClass);
    // Also runs when the chart unmounts mid-full-screen (on a navigation, say),
    // which is what stops the header from staying hidden for the rest of the
    // session.
    return () => {
      setIsFullScreenMode?.(false);
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove(fullScreenBodyClass);
    };
  }, [isFullScreen, setIsFullScreenMode]);

  useEffect(() => {
    if (!isFullScreen) return;
    const collapseOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // This listener is registered as the chart expands, before any popup
      // registers its own, so it runs while a popup that is closing on this
      // same keypress is still open in the DOM. That is what lets the keypress
      // be read as the popup's: the first Escape dismisses the menu, and a
      // second one collapses the chart.
      if (document.querySelector(openPopupSelector)) return;
      setIsFullScreen(false);
    };
    window.addEventListener("keydown", collapseOnEscape);
    return () => window.removeEventListener("keydown", collapseOnEscape);
  }, [isFullScreen]);

  useEffect(() => {
    // Plotly reads its container's size when it draws, so it has to be told
    // once the browser has laid the overlay out — hence the frame's wait.
    //
    // A resize event rather than a call on the plot's own handler: every chart
    // is rendered with `useResizeHandler`, which is what puts that handler on
    // the window in the first place, so this reaches the plot by the route it
    // already listens on. It also reaches plots this hook has no way to hold a
    // ref to — a dashboard widget's panel is handed its chart as children and
    // never sees the plot inside it.
    const frame = requestAnimationFrame(() =>
      window.dispatchEvent(new Event("resize"))
    );
    return () => cancelAnimationFrame(frame);
  }, [isFullScreen]);

  /**
   * Portalled to <body> rather than positioned where it sits: a fixed overlay
   * resolves against the nearest transformed ancestor instead of the viewport,
   * and the dashboard grid gives every tile a transform. The move remounts the
   * chart, which is acceptable for a deliberate action and would not be for
   * anything that happens on a resize.
   */
  const renderFullScreen = (panel: React.ReactElement) =>
    isFullScreen
      ? createPortal(
          <div className={"chart-full-screen"}>{panel}</div>,
          document.body
        )
      : panel;

  return { isFullScreen, toggleFullScreen, renderFullScreen };
};
